import { venueKeys } from "@greeter-venue/api/QueryKeys";
import { useLoading } from "@greeter/loading";
import { UpdateVenueRequest } from "@greeter/venue-api";
import { useIonViewWillEnter } from "@ionic/react";
import { useMemo } from "react";
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { EditVenuePage } from "./EditVenuePage";
import { SpecialOpeningHours } from "@greeter/core";
import { Query } from "@greeter/query";
import { useGreeterVenueApi } from "@greeter/matter";
import { Log } from "@greeter/log";

export const EditVenuePageApiHandler: React.FC = () => {
  const { api: venueApi } = useGreeterVenueApi();

  const venueQuery = useQuery({
    queryKey: venueKeys.edit(),
    async queryFn() {
      return await venueApi.fetchVenue();
    },
    staleTime: Infinity,
  });

  const priceCapacitiesQuery = useQuery({
    queryKey: ["pricedCapacities"],
    queryFn: async () => await venueApi.fetchPricedCapacities(),
    enabled: !!venueQuery.data,
    staleTime: Infinity,
  });

  const specialOpeningHoursQuery: UseQueryResult<SpecialOpeningHours[], Error> =
    useQuery({
      queryKey: ["specialOpeningHours", venueQuery.data?.id],
      queryFn: async () => await venueApi.fetchSpecialOpeningHours(),
      enabled: !!venueQuery.data,
      staleTime: Infinity,
      refetchInterval: false,
    });

  useIonViewWillEnter(() => {
    Log.debug(
      "[EditVenuePageApiHandler] refetching venue and special opening hours"
    );
    venueQuery
      .refetch({ cancelRefetch: true })
      .then(() => specialOpeningHoursQuery.refetch({ cancelRefetch: true }));
  });

  const simpleSpecialOpeningHoursQuery = useMemo(
    () => Query.mapQuery(specialOpeningHoursQuery),
    [specialOpeningHoursQuery.data]
  );

  const musicGenresQuery = useQuery({
    queryKey: ["musicGenres"],
    queryFn: async () => await venueApi.fetchMusicGenres({ pageSize: 100 }),
    staleTime: Infinity,
  });

  const themesQuery = useQuery({
    queryKey: ["themes"],
    queryFn: async () => await venueApi.fetchThemes(),
    staleTime: Infinity,
  });

  const updateVenueMutation = useMutation({
    mutationFn: (request: UpdateVenueRequest) => venueApi.updateVenue(request),
  });

  const updateSpecialOpeningHoursMutation = useMutation({
    mutationFn: (specialOpeningHours: SpecialOpeningHours[]) =>
      venueApi.updateSpecialOpeningHours({ specialOpeningHours }),
  });

  const { waitFor } = useLoading();

  async function update(request) {
    const actualRequest: UpdateVenueRequest = { ...request };

    if (request.cover) {
      actualRequest.coverId = (
        await waitFor(
          venueApi.uploadAsset({ data: request.cover }),
          "Uploader ny cover billede"
        )
      ).id;
    }
    if (request.logo) {
      actualRequest.logoId = (
        await waitFor(
          venueApi.uploadAsset({ data: request.logo }),
          "Uploader nyt logo"
        )
      ).id;
    }

    await waitFor(
      updateVenueMutation.mutateAsync(actualRequest),
      "Opdaterer informationer"
    );
    queryClient.invalidateQueries({ queryKey: venueKeys.self() });
    venueQuery.refetch();
  }

  const queryClient = useQueryClient();

  return (
    <EditVenuePage
      availableThemes={themesQuery.data ?? []}
      availableMusicGenres={musicGenresQuery.data ?? []}
      pricedCapacities={priceCapacitiesQuery.data ?? []}
      venue={venueQuery.data}
      specialOpeningHours={simpleSpecialOpeningHoursQuery}
      onUpdate={update}
      onSpecialOpeningHoursUpdate={async (soh) => {
        await updateSpecialOpeningHoursMutation.mutateAsync(soh);
        await queryClient.invalidateQueries({
          queryKey: ["specialOpeningHours", venueQuery.data?.id],
        });
      }}
    />
  );
};
